import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFavorites } from './useFavorites';

export interface Remixer {
  user_id: string;
  user_initials: string;
}

export interface LibraryPrompt {
  score_id: string;
  submission_id?: string;
  overall_score: number;
  original_score: number | null;
  detected_type: string | null;
  best_prompt: string | null;
  original_content: string | null;
  submission_type: string | null;
  dimension_scores_json: Record<string, any> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  points_total: number | null;
  points_base: number | null;
  points_rubric: number | null;
  points_bonus: number | null;
  created_at: string;
  tags: string[];
  favorite_count: number;
  remix_count: number;
  recent_remixers: Remixer[];
  is_public: boolean;
}

export interface LibraryDraft {
  conversation_id: string;
  initial_goal: string | null;
  messages_json: any[];
  turn_count: number;
  started_at: string;
  status: string;
  final_prompt: string | null;
}

export function useLibraryRecent(userId: string | undefined) {
  return useQuery({
    queryKey: ['library-recent', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase.rpc('get_user_library_recent', { 
        _user_id: userId,
        _limit: 50
      });
      
      if (error) {
        console.error('Error fetching library recent:', error);
        return [];
      }
      
      // Transform the data to ensure proper typing
      return (data || []).map((item: any) => ({
        ...item,
        recent_remixers: Array.isArray(item.recent_remixers) ? item.recent_remixers : [],
        favorite_count: item.favorite_count || 0,
        remix_count: item.remix_count || 0,
        is_public: item.is_public ?? false,
      })) as LibraryPrompt[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useLibraryDrafts(userId: string | undefined) {
  return useQuery({
    queryKey: ['library-drafts', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase.rpc('get_user_drafts', { 
        _user_id: userId
      });
      
      if (error) {
        console.error('Error fetching library drafts:', error);
        return [];
      }
      
      return ((data as LibraryDraft[]) || []).filter((draft) => draft.status !== 'abandoned');
    },
    enabled: !!userId,
    staleTime: 1000 * 30, // 30 seconds
  });
}

export function useLibraryData(userId: string | undefined) {
  const recentQuery = useLibraryRecent(userId);
  const draftsQuery = useLibraryDrafts(userId);
  const favoritesQuery = useFavorites(userId);

  return {
    recent: recentQuery.data || [],
    drafts: draftsQuery.data || [],
    favorites: favoritesQuery.data || [],
    isLoading: recentQuery.isLoading || draftsQuery.isLoading || favoritesQuery.isLoading,
    counts: {
      recent: recentQuery.data?.length || 0,
      drafts: draftsQuery.data?.length || 0,
      favorites: favoritesQuery.data?.length || 0,
    }
  };
}
